import { useRef } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useAuth } from './auth/auth0-provider'
import { NotFound } from './pages/NotFound'
import { SiteRoute } from './pages/SiteRoute'
import { SiteDetailOverview } from './pages/SiteDetailOverview'
import { SiteDetailEvents } from './pages/SiteDetailEvents'
import { SiteDetailAnalytics } from './pages/SiteDetailAnalytics'
import { Profile } from './pages/Profile'
import { Settings } from './pages/Settings'
import Pricing from './pages/Pricing'
import Billing from './pages/Billing'
import Login from './components/Login'
import { Auth0Callback } from './components/Auth0Callback'
import { Auth0IntegrationTest } from './components/Auth0IntegrationTest'
import Templates from './pages/Templates'
import Sites from './pages/Sites'
import { NewSite } from './pages/NewSite'
import { EditSite } from './pages/EditSite'
import Domains from './pages/Domains'
import Users from './pages/Users'
import InvitationLanding from './pages/InvitationLanding'
import InviteOnly from './pages/InviteOnly'
import Invites from './pages/Invites'
import UserDetail from './pages/UserDetail'
import { ThemeProvider, SidebarProvider, SidebarLayout } from './components/sidebar'
import navigationConfig from './config/navigation'
import { BreadcrumbProvider } from './contexts/BreadcrumbContext'
import { DocumentTitle } from './components/DocumentTitle'
import { Skeleton } from './components/ui/skeleton'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './lib/query-client'

// Module-scope on purpose: defining these inside App() gives them a new
// function identity on every App render, which React treats as a different
// component type — unmounting and remounting the ENTIRE protected subtree on
// any auth-context churn. That remount loop is what turned the invites 403
// into a request storm.
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isSdkReady } = useAuth();
  const location = useLocation();
  const hasRenderedChildren = useRef(false);

  // Gate only until children have mounted. After that, a transient flicker in
  // isAuthenticated / isSdkReady (Auth0 token refresh, provider re-init) must
  // NOT swap children for the loader or login redirect — that unmounts and
  // remounts the whole page subtree. A real logout navigates away on its own.
  if (!hasRenderedChildren.current) {
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    if (!isSdkReady) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Skeleton className="h-8 w-48" />
        </div>
      );
    }
    hasRenderedChildren.current = true;
  }
  return <>{children}</>;
};

// Redirects legacy deployment routes to sites
const DeploymentRedirect: React.FC = () => {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  return <Navigate to={`/sites/${deploymentId}`} replace />;
};

function App(): React.JSX.Element {
  const { isLoading, isAuthenticated } = useAuth0();

  // Invite redemption is sequenced ahead of the app by RedemptionGate (mounted
  // above AppCacheProvider in main.tsx), so it no longer races the authed
  // bootstrap calls. See src/auth/RedemptionGate.tsx.

  if (isLoading) {
    return (
      <div className="min-h-screen flex bg-background">
        {/* Sidebar skeleton */}
        <div className="hidden md:flex md:w-64 md:flex-col border-r bg-surface">
          <div className="flex flex-col flex-1 p-4 space-y-4">
            <Skeleton className="h-8 w-32" />
            <div className="space-y-2 mt-6">
              <Skeleton className="h-9 w-full rounded-full" />
              <Skeleton className="h-9 w-full rounded-full" />
              <Skeleton className="h-9 w-full rounded-full" />
              <Skeleton className="h-9 w-full rounded-full" />
            </div>
          </div>
          <div className="p-4 border-t">
            <Skeleton className="h-10 w-full rounded-full" />
          </div>
        </div>
        {/* Main content skeleton */}
        <div className="flex-1 p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            <Skeleton className="h-10 w-48" />
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Skeleton className="h-48 rounded-2xl" />
              <Skeleton className="h-48 rounded-2xl" />
              <Skeleton className="h-48 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light">
      <SidebarProvider config={navigationConfig}>
        <BreadcrumbProvider>
        <DocumentTitle />
        <Routes>
          {/* Public routes - no sidebar */}
          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/" replace /> : <Login />
          } />

          {/* Auth0 callback route - no sidebar */}
          <Route path="/callback" element={<Auth0Callback />} />

          {/* Public invitation landing - no auth required */}
          <Route path="/invitation/:token" element={<InvitationLanding />} />

          {/* Invite-only gate screen — shown when INVITE_ONLY_MODE blocks signup */}
          <Route path="/invite-only" element={<InviteOnly />} />

          {/* Protected routes with sidebar layout */}
          <Route path="*" element={
            <ProtectedRoute>
              <SidebarLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/sites" replace />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/sites" element={<Sites />} />
                  <Route path="/sites/new" element={<NewSite />} />
                  <Route path="/sites/:siteId" element={<SiteRoute />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<SiteDetailOverview />} />
                    <Route path="activity" element={<SiteDetailEvents />} />
                    <Route path="analytics" element={<SiteDetailAnalytics />} />
                  </Route>
                  <Route path="/sites/:siteId/edit" element={<EditSite />} />
                  {/* Legacy redirects for backward compatibility */}
                  <Route path="/deployments/new" element={<Navigate to="/sites/new" replace />} />
                  <Route path="/deployments/:deploymentId" element={<DeploymentRedirect />} />
                  <Route path="/domains" element={<Domains />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/users/:userId" element={<UserDetail />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/invites" element={<Invites />} />
                  {/* Debug-only auth harness — never shipped to production users */}
                  {import.meta.env.DEV && (
                    <Route path="/auth0-test" element={<Auth0IntegrationTest />} />
                  )}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </SidebarLayout>
            </ProtectedRoute>
          } />
        </Routes>
        </BreadcrumbProvider>
      </SidebarProvider>
    </ThemeProvider>
    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

export default App